import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import enTranslations from '../translations/en';
import frTranslations from '../translations/fr';
import { Purchases } from '@revenuecat/purchases-js';

const PremiumPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const t = language === 'fr' ? frTranslations : enTranslations;
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rcInitialized, setRcInitialized] = useState(false);
  const [purchasesInstance, setPurchasesInstance] = useState<any>(null);
  const [availablePackages, setAvailablePackages] = useState<any[]>([]);
  
  const userId = searchParams.get('user_id')?.trim() || null;
  const email = searchParams.get('email')?.trim() || null;
  const returnTo = searchParams.get('return_to');

  useEffect(() => {
    if (!userId) {
      setError(t.premium?.noUserIdError || 'No user ID provided');
      return;
    }

    // Initialize RevenueCat
    const initRevenueCat = async () => {
      try {
        // Use production or sandbox key based on environment
        const apiKey = (import.meta as any).env.PROD 
          ? 'rcb_rUiRkroaPcEZWKQohYCdgEjkpWbL'  // Production
          : 'rcb_sb_KPmZBdRiiUYhupiLTHkXMflRL'; // Sandbox
        
        console.log('Initializing RevenueCat...');
        console.log('Purchases class:', Purchases);
        
        // Configure and get the singleton instance
        const instance = await Purchases.configure({
          apiKey: apiKey,
          appUserId: userId
        });
        
        console.log('RevenueCat instance:', instance);
        console.log('Instance methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(instance)));
        
        setPurchasesInstance(instance);
        setRcInitialized(true);
        console.log('RevenueCat initialized successfully');
      } catch (err) {
        console.error('Failed to initialize RevenueCat:', err);
        setError('Failed to initialize payment system');
      }
    };

    initRevenueCat();
  }, [userId, t]);

  // Fetch offerings (and specifically the 2026 offering) once RevenueCat is ready
  useEffect(() => {
    if (!rcInitialized || !purchasesInstance) return;

    const loadOfferings = async () => {
      try {
        const offerings = await purchasesInstance.getOfferings();
        console.log('Available offerings (initial load):', offerings);

        const offering =
          offerings.all['2026'] ||
          offerings.current ||
          offerings.all[Object.keys(offerings.all)[0]];

        if (offering && Array.isArray(offering.availablePackages)) {
          setAvailablePackages(offering.availablePackages);
        } else {
          setAvailablePackages([]);
        }
      } catch (err) {
        console.error('Failed to load offerings:', err);
      }
    };

    loadOfferings();
  }, [rcInitialized, purchasesInstance]);

  const handleCheckout = async (packageIdentifier?: string): Promise<void> => {
    if (!userId || !rcInitialized || !purchasesInstance) {
      setError(t.premium?.noUserIdError || 'Payment system not ready');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get offerings from RevenueCat instance (fresh each checkout)
      const offerings = await purchasesInstance.getOfferings();
      console.log('Available offerings (checkout):', offerings);
      
      // Get the 2026 offering or fall back to current / first available
      const offering =
        offerings.all['2026'] ||
        offerings.current ||
        offerings.all[Object.keys(offerings.all)[0]];
      
      if (!offering || !offering.availablePackages || offering.availablePackages.length === 0) {
        throw new Error('No subscription packages available');
      }

      // Choose the package to purchase: either the one matching the identifier, or the first one
      let packageToPurchase = offering.availablePackages[0];
      if (packageIdentifier) {
        const matched = offering.availablePackages.find(
          (pkg: any) => pkg.identifier === packageIdentifier
        );
        if (matched) {
          packageToPurchase = matched;
        }
      }
      
      console.log('Purchasing package:', packageToPurchase);

      // Initiate purchase
      const { customerInfo } = await purchasesInstance.purchase({
        rcPackage: packageToPurchase,
        email: email || undefined,
      });

      console.log('Purchase completed:', customerInfo);
      
      // Redirect to success page
      const successUrl = returnTo === 'extension' 
        ? `/${language}/premium/success?return_to=extension`
        : `/${language}/premium/success`;
      
      window.location.href = successUrl;
      
    } catch (err) {
      console.error('Checkout error:', err);
      
      if (err && typeof err === 'object' && 'errorCode' in err) {
        if ((err as any).errorCode === 1) { // User cancelled
          setError(null);
          setLoading(false);
          return;
        }
      }
      
      setError(err instanceof Error ? err.message : t.premium?.checkoutError || 'Failed to start checkout');
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#feffdd',
      padding: '1rem 1rem 2rem 1rem',
      fontFamily: "'Kode Mono', monospace",
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      paddingTop: '4rem'
    }}>
      <div style={{
        maxWidth: '500px',
        width: '100%',
        background: '#fefddd',
        border: '4px solid #000',
        borderRadius: '16px',
        padding: '2rem 1.5rem',
        boxShadow: '12px 12px 0px #000',
        position: 'relative'
      }}>
        {/* Premium Tag/Label */}
        <div style={{
          position: 'absolute',
          top: '-4px',
          right: '2.5rem',
          zIndex: 10,
          background: '#f4c95d',
          border: '4px solid #000',
          borderTop: 'none',
          borderRadius: '0 0 8px 8px',
          padding: '1rem 1.25rem 0.6rem 1.25rem',
          fontWeight: 'bold',
          fontSize: '0.8rem',
          textTransform: 'uppercase',
          textAlign: 'center',
          boxShadow: '0 3px 5px rgba(0, 0, 0, 0.2)'
        }}>
          Premium
        </div>

        {/* Title */}
        <h1 style={{
          fontSize: 'clamp(1rem, 3vw, 1.25rem)',
          fontWeight: 'normal',
          marginBottom: '2rem',
          color: '#666',
          fontFamily: "'Kode Mono', monospace",
          lineHeight: '1.4',
          paddingRight: '6rem'
        }}>
          {t.premium?.subtitle || 'Débloquez toutes les fonctionnalités premium'}
        </h1>

        {/* Features List */}
        <div style={{
          marginBottom: '2rem'
        }}>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {(t.premium?.features || []).map((feature, index) => (
              <li key={index} style={{
                fontSize: '0.875rem',
                marginBottom: index === (t.premium?.features?.length || 0) - 1 ? '0' : '0.875rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                fontFamily: "'Kode Mono', monospace",
                color: '#000',
                lineHeight: '1.5'
              }}>
                <span style={{ 
                  fontSize: '1.25rem',
                  lineHeight: '1',
                  flexShrink: 0
                }}>{index === 0 ? '😊' : '🗺️'}</span>
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Plans from 2026 offering */}
        {availablePackages.length > 0 && (
          <div style={{ marginBottom: '2rem' }}>
            <h2 style={{
              fontSize: '0.9rem',
              marginBottom: '0.75rem',
              fontFamily: "'Kode Mono', monospace",
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: '#555'
            }}>
              {'plansTitle' in (t.premium || {}) ? (t.premium as any).plansTitle : 'Choose your plan'}
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {availablePackages.map((pkg: any) => {
                const billingProduct = pkg.webBillingProduct || pkg.rcBillingProduct || {};
                const identifier: string = pkg.identifier ?? billingProduct.identifier ?? 'plan';
                const name: string = billingProduct.displayName || billingProduct.title || identifier;

                const priceObj = billingProduct.price || billingProduct.currentPrice || {};
                const price: string | undefined = priceObj.formattedPrice;

                // Determine plan type (weekly / monthly / annual) from packageType or period
                let planLabel = '';
                const packageType: string | undefined = pkg.packageType;
                const normalPeriod = billingProduct.normalPeriodDuration || '';
                const period = billingProduct.period || {};
                const periodUnit: string | undefined = period.unit;

                if (packageType === '$rc_weekly' || periodUnit === 'week' || normalPeriod.includes('W')) {
                  planLabel = language === 'fr' ? 'Hebdomadaire' : 'Weekly';
                } else if (packageType === '$rc_monthly' || periodUnit === 'month' || normalPeriod.includes('M')) {
                  planLabel = language === 'fr' ? 'Mensuel' : 'Monthly';
                } else if (packageType === '$rc_annual' || periodUnit === 'year' || normalPeriod.includes('Y')) {
                  planLabel = language === 'fr' ? 'Annuel' : 'Annual';
                }

                // Free trial info
                let trialLabel: string | null = null;
                const freeTrial = billingProduct.freeTrialPhase;
                if (freeTrial && freeTrial.periodDuration) {
                  // Very small parser for ISO-8601 periods like P1W, P2W, P7D etc.
                  const dur = String(freeTrial.periodDuration);
                  let trialText = '';
                  if (dur.startsWith('P') && dur.endsWith('W')) {
                    const weeks = parseInt(dur.slice(1, -1), 10) || 1;
                    if (language === 'fr') {
                      trialText = weeks === 1 ? '1 semaine d’essai gratuite' : `${weeks} semaines d’essai gratuites`;
                    } else {
                      trialText = weeks === 1 ? '1‑week free trial' : `${weeks}-week free trial`;
                    }
                  } else if (dur.startsWith('P') && dur.endsWith('D')) {
                    const days = parseInt(dur.slice(1, -1), 10) || 1;
                    if (language === 'fr') {
                      trialText = days === 1 ? '1 jour d’essai gratuit' : `${days} jours d’essai gratuits`;
                    } else {
                      trialText = days === 1 ? '1‑day free trial' : `${days}-day free trial`;
                    }
                  }
                  trialLabel = trialText || (language === 'fr' ? 'Essai gratuit inclus' : 'Free trial included');
                }

                return (
                  <button
                    key={identifier}
                    disabled={loading || !userId}
                    onClick={(event) => {
                      event.preventDefault();
                      void handleCheckout(identifier);
                    }}
                    style={{
                      width: '100%',
                      padding: 0,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'stretch',
                      borderRadius: '12px',
                      border: '4px solid #000',
                      background: 'transparent',
                      boxShadow: loading || !userId ? 'none' : '6px 6px 0px #000',
                      cursor: loading || !userId ? 'not-allowed' : 'pointer',
                      fontFamily: "'Kode Mono', monospace",
                      overflow: 'hidden',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      if (!loading && userId) {
                        e.currentTarget.style.transform = 'translate(-2px, -2px)';
                        e.currentTarget.style.boxShadow = '8px 8px 0px #000';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loading && userId) {
                        e.currentTarget.style.transform = 'translate(0, 0)';
                        e.currentTarget.style.boxShadow = '6px 6px 0px #000';
                      }
                    }}
                    onMouseDown={(e) => {
                      if (!loading && userId) {
                        e.currentTarget.style.transform = 'translate(2px, 2px)';
                        e.currentTarget.style.boxShadow = '4px 4px 0px #000';
                      }
                    }}
                    onMouseUp={(e) => {
                      if (!loading && userId) {
                        e.currentTarget.style.transform = 'translate(-2px, -2px)';
                        e.currentTarget.style.boxShadow = '8px 8px 0px #000';
                      }
                    }}
                  >
                    <div style={{
                      flex: 1,
                      padding: '1rem 1.25rem',
                      background: loading || !userId ? '#ccc' : '#f4c95d',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      gap: '0.15rem',
                    }}>
                      {planLabel && (
                        <span style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {planLabel}
                        </span>
                      )}
                    </div>
                    <div style={{
                      padding: '1rem 1.25rem',
                      background: '#feffdd',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      gap: '0.15rem',
                      minWidth: '120px'
                    }}>
                      {price && (
                        <span style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                          {price}
                        </span>
                      )}
                      {trialLabel && (
                        <span style={{ fontSize: '0.7rem', opacity: 0.8 }}>
                          {trialLabel}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Cancel anytime note */}
        <p style={{ 
          fontSize: '0.75rem', 
          color: '#666',
          fontFamily: "'Kode Mono', monospace",
          margin: '0 0 1rem 0',
          textAlign: 'center',
          lineHeight: '1.4'
        }}>
          ✓ {t.premium?.cancelAnytime || 'Résiliez à tout moment'}
        </p>

        {/* Extension Note */}
        {returnTo === 'extension' && (
          <p style={{
            marginTop: '1rem',
            fontSize: '0.75rem',
            color: '#666',
            fontFamily: "'Kode Mono', monospace",
            lineHeight: '1.4',
            textAlign: 'center'
          }}>
            💡 Après le paiement, retournez à l'extension
          </p>
        )}
      </div>
    </div>
  );
};

export default PremiumPage;
